import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, LinearProgress, Chip, TextField, Tooltip,
  CircularProgress, IconButton
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { invoiceService, customerService, productService, exchangeRateService, batchService, gatePassService } from '../../services/financeService';
import { API_BASE } from '../../utils/apiUtils';
import { buildInvoiceRows } from '../../utils/exportUtils';
import { checkAdminStatus } from '../../utils/authUtils';
import AttachmentPanel from './AttachmentPanel';
import PaymentModeFields, { PAYMENT_MODES } from './PaymentModeFields';
import SearchableSelect from './SearchableSelect';
import CsvActions from '../common/CsvActions';
import PdfActions from '../common/PdfActions';
import './Financial.css';

const fmt = (v) =>
  v != null
    ? Number(v).toLocaleString('en-US', { minimumFractionDigits: 0 })
    : '0';

// ─── Credit Status Banner ──────────────────────────────────────────────────
const CreditBanner = ({ status, loading, thisInvoicePkr = 0, thisInvoiceCurrency, thisInvoiceTotal = 0, exchangeRate }) => {
  if (loading) return (
    <div style={{ padding: '8px 0' }}>
      <LinearProgress />
    </div>
  );
  if (!status) return null;

  const { creditLimit, currentOutstanding, exchangeRateUsed, usdOutstanding, overdueInvoiceCount,
          usdCreditLimit, usdAvailableCredit, usdLimitExceeded } = status;

  if (!creditLimit && !usdCreditLimit && !currentOutstanding && !thisInvoicePkr) return null;

  const projectedOutstanding = (Number(currentOutstanding) || 0) + thisInvoicePkr;
  const projectedAvailable = creditLimit ? Number(creditLimit) - projectedOutstanding : null;
  const projectedExceeded = creditLimit ? projectedOutstanding >= Number(creditLimit) : false;
  const projectedUsage = creditLimit && Number(creditLimit) > 0
    ? Math.min(Math.round(projectedOutstanding / Number(creditLimit) * 100), 100)
    : null;
  const projectedWarning = !projectedExceeded && projectedUsage >= 80;

  const color = projectedExceeded ? 'error' : projectedWarning ? 'warning' : overdueInvoiceCount > 0 ? 'warning' : 'success';
  const icon = projectedExceeded ? <ErrorOutlineIcon fontSize="small" /> : projectedWarning ? <WarningAmberIcon fontSize="small" /> : <CheckCircleOutlineIcon fontSize="small" />;

  return (
    <Alert
      severity={color === 'success' ? 'success' : color === 'warning' ? 'warning' : 'error'}
      icon={icon}
      sx={{ mb: 2, py: 0.5 }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {/* Row 1: existing outstanding */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
          {creditLimit ? (
            <>
              <span>Credit Limit: <strong>PKR {fmt(creditLimit)}</strong></span>
              <span>Current Outstanding: <strong>PKR {fmt(currentOutstanding)}</strong></span>
              {usdOutstanding > 0 && exchangeRateUsed && (
                <span style={{ fontSize: '0.85em', opacity: 0.85 }}>
                  (incl. USD {fmt(usdOutstanding)} @ {Number(exchangeRateUsed).toFixed(2)})
                </span>
              )}
            </>
          ) : (
            <span>Outstanding balance: <strong>PKR {fmt(currentOutstanding)}</strong> (no credit limit set)</span>
          )}
          {overdueInvoiceCount > 0 && (
            <Chip label={`${overdueInvoiceCount} overdue`} color="error" size="small" />
          )}
        </div>

        {/* Row 2: live projected impact from this invoice */}
        {thisInvoicePkr > 0 && creditLimit && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', borderTop: '1px dashed var(--color-border-strong)', paddingTop: 6 }}>
            <span style={{ fontWeight: 600 }}>
              + This invoice: {thisInvoiceCurrency === 'USD' && exchangeRate
                ? <span>USD {fmt(thisInvoiceTotal)} = <strong>PKR {fmt(thisInvoicePkr)}</strong> <span style={{ fontSize: '0.8em', opacity: 0.7 }}>@ {exchangeRate.toFixed(2)}</span></span>
                : <span><strong>PKR {fmt(thisInvoicePkr)}</strong></span>}
            </span>
            <span>
              Projected: <strong>PKR {fmt(projectedOutstanding)}</strong>
            </span>
            <span>
              {projectedExceeded
                ? <span style={{ color: 'var(--color-error)', fontWeight: 700 }}>WILL EXCEED LIMIT by PKR {fmt(Math.abs(projectedAvailable))}</span>
                : <span>Available after: <strong>PKR {fmt(projectedAvailable)}</strong> ({projectedUsage}% used)</span>}
            </span>
          </div>
        )}

        {/* Warn when USD invoice has no exchange rate — credit impact unknown */}
        {thisInvoiceCurrency === 'USD' && !exchangeRate && thisInvoiceTotal > 0 && (
          <div style={{ display:'flex', alignItems:'center', gap:6, borderTop:'1px dashed var(--color-border-strong)', paddingTop:6, color:'var(--color-warning)', fontWeight:600, fontSize:'0.85em' }}>
            <WarningAmberIcon fontSize="small" />
            USD invoice — no exchange rate found. Credit limit impact cannot be calculated.
            Go to <strong>Exchange Rates</strong> and add today's rate.
          </div>
        )}

        {/* When no items yet, just show current status */}
        {thisInvoicePkr === 0 && creditLimit && !(thisInvoiceCurrency === 'USD' && !exchangeRate && thisInvoiceTotal > 0) && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
            <span>
              {Number(currentOutstanding) >= Number(creditLimit)
                ? <span style={{ color: 'var(--color-error)', fontWeight: 700 }}>OVER LIMIT by PKR {fmt(Math.abs(Number(creditLimit) - Number(currentOutstanding)))}</span>
                : <span>Available: <strong>PKR {fmt(Number(creditLimit) - Number(currentOutstanding))}</strong> ({Math.min(Math.round(Number(currentOutstanding) / Number(creditLimit) * 100), 100)}% used)</span>}
            </span>
          </div>
        )}

        {/* USD credit limit row */}
        {usdCreditLimit && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center', borderTop: '1px dashed var(--color-border-strong)', paddingTop: 6 }}>
            <span>USD Limit: <strong>USD {fmt(usdCreditLimit)}</strong></span>
            <span>USD Outstanding: <strong>USD {fmt(usdOutstanding || 0)}</strong></span>
            <span>
              {usdLimitExceeded
                ? <span style={{ color: 'var(--color-error)', fontWeight: 700 }}>USD OVER LIMIT by {fmt(Math.abs(Number(usdAvailableCredit)))}</span>
                : <span>USD Available: <strong>USD {fmt(usdAvailableCredit)}</strong></span>}
            </span>
          </div>
        )}
      </div>
    </Alert>
  );
};

// ─── Confirm Dialog ────────────────────────────────────────────────────────
const ConfirmDialog = ({ open, title, message, onConfirm, onCancel, confirmLabel = 'Confirm', danger = false }) => (
  <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
    <DialogTitle sx={{ pb: 1 }}>{title}</DialogTitle>
    <DialogContent>
      <p style={{ margin: 0, color: 'var(--color-neutral-700)' }}>{message}</p>
    </DialogContent>
    <DialogActions sx={{ px: 3, pb: 2 }}>
      <button onClick={onCancel} className="btn-secondary">Cancel</button>
      <button onClick={onConfirm} className={danger ? 'btn-danger' : 'btn-primary'}>{confirmLabel}</button>
    </DialogActions>
  </Dialog>
);

// ─── Payment Dialog ────────────────────────────────────────────────────────
const PaymentDialog = ({ open, invoice, onClose, onSave }) => {
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState('');
  const [notes, setNotes] = useState('');
  const [mode, setMode] = useState('CASH');
  const [details, setDetails] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const attachRef = useRef(null);

  useEffect(() => {
    if (open && invoice) {
      setAmount(String(invoice.balanceDue || ''));
      setPaymentDate(new Date().toISOString().split('T')[0]);
      setNotes('');
      setMode('CASH');
      setDetails({});
      setReceipt(null);
      setShowDetail(false);
    }
  }, [open, invoice]);

  const handleSubmit = async () => {
    if (!amount || parseFloat(amount) <= 0) { setError('Enter a valid amount'); return; }
    setSaving(true);
    setError(null);
    const paidAmount = parseFloat(amount);
    try {
      const res = await invoiceService.recordPayment(invoice.id, {
        amount: paidAmount,
        paymentDate: paymentDate || null,
        notes,
        paymentMode: mode,
        paymentDetails: details,
      });
      const cashTxnId = res?.data?.lastPaymentCashTransactionId;
      if (cashTxnId && attachRef.current?.hasPending()) {
        await attachRef.current.uploadPending('CASH', cashTxnId);
      }
      // Show a plain-language receipt instead of just closing the modal — the ledger entry
      // was posted correctly the whole time, but nothing ever confirmed that back to the user.
      setReceipt({
        amount: paidAmount,
        modeLabel: PAYMENT_MODES.find(m => m.value === mode)?.label || mode,
        customerName: invoice.customerName,
        currency: invoice.currency || 'PKR',
        newBalance: res?.data?.balanceDue,
        newStatus: res?.data?.status,
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDone = () => { setReceipt(null); onSave(); };

  if (receipt) {
    return (
      <Dialog open={open} onClose={handleDone} maxWidth="xs" fullWidth>
        <DialogContent sx={{ pt: 3, textAlign: 'center' }}>
          <div className="notice-mark notice-mark-success" aria-hidden="true">✓</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-success)', marginBottom: 4 }}>Payment Recorded</div>
          <p style={{ color: 'var(--color-neutral-700)', fontSize: 14, margin: '10px 0' }}>
            <strong>{receipt.currency} {fmt(receipt.amount)}</strong> received from <strong>{receipt.customerName}</strong> via {receipt.modeLabel}
          </p>
          <div style={{ background: 'var(--color-success-light)', border: '1px solid var(--color-success-border)', borderRadius: 8, padding: '12px 16px', margin: '12px 0', textAlign: 'left' }}>
            <div style={{ fontSize: 12, color: 'var(--color-success-strong)' }}>Outstanding balance now</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: receipt.newBalance > 0 ? 'var(--color-error)' : 'var(--color-success)' }}>
              {receipt.currency} {fmt(receipt.newBalance)}
            </div>
            {receipt.newStatus === 'PAID' && <div style={{ fontSize: 12, color: 'var(--color-success)', marginTop: 2, fontWeight: 600 }}>Fully paid — invoice closed</div>}
          </div>
          <button
            onClick={() => setShowDetail(s => !s)}
            style={{ background: 'none', border: 'none', color: 'var(--color-text-secondary)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
          >
            {showDetail ? 'Hide' : 'View'} accounting detail
          </button>
          {showDetail && (
            <div style={{ marginTop: 10, textAlign: 'left', fontSize: 12, color: 'var(--color-neutral-700)', background: 'var(--color-neutral-50)', border: '1px solid var(--color-border-medium)', borderRadius: 8, padding: 10 }}>
              <div>• Cash increased by {receipt.currency} {fmt(receipt.amount)}</div>
              <div>• Amount {receipt.customerName} owes you decreased by {receipt.currency} {fmt(receipt.amount)}</div>
              <div style={{ color: 'var(--color-text-tertiary)', marginTop: 4 }}>Posted to Chart of Accounts — see Payments & Cash for the full entry.</div>
            </div>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'center' }}>
          <button onClick={handleDone} className="btn-primary">Done</button>
        </DialogActions>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Record Payment</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <p style={{ margin: '0 0 12px', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
          Invoice: <strong>{invoice?.invoiceNumber}</strong> — Balance Due: <strong>{invoice?.currency || 'PKR'} {fmt(invoice?.balanceDue)}</strong>
        </p>
        <TextField
          label={`Payment Amount (${invoice?.currency || 'PKR'})`}
          type="number"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          fullWidth
          size="small"
          inputProps={{ min: 0.01, step: 0.01 }}
          autoFocus
          sx={{ mb: 2 }}
        />
        <TextField
          label="Payment Date"
          type="date"
          value={paymentDate}
          onChange={e => setPaymentDate(e.target.value)}
          fullWidth
          size="small"
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 2 }}
        />
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 11, color: 'var(--color-text-secondary)', display: 'block', marginBottom: 3 }}>Payment Mode</label>
          <select
            value={mode}
            onChange={e => { setMode(e.target.value); setDetails({}); }}
            style={{ width: '100%', padding: '8px', border: '1px solid var(--color-border-strong)', borderRadius: 6, fontSize: 14 }}
          >
            {PAYMENT_MODES.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
          </select>
        </div>
        <PaymentModeFields mode={mode} details={details} onChange={setDetails} />
        <TextField
          label="Narration / Notes"
          value={notes}
          onChange={e => setNotes(e.target.value)}
          fullWidth
          size="small"
          multiline
          minRows={2}
          sx={{ mb: 1 }}
        />
        <AttachmentPanel ref={attachRef} entityType="CASH" entityId={null} />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <button onClick={onClose} className="btn-secondary" disabled={saving}>Cancel</button>
        <button onClick={handleSubmit} className="btn-primary" disabled={saving}>
          {saving ? 'Saving...' : 'Record Payment'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Component ────────────────────────────────────────────────────────
const InvoiceManagement = () => {
  const { isAdmin } = checkAdminStatus(localStorage.getItem('userData'));
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Confirm dialog state
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, danger: false });
  // Payment dialog state
  const [paymentDialog, setPaymentDialog] = useState({ open: false, invoice: null });
  // Cancel with reason dialog state
  const [cancelDialog, setCancelDialog] = useState({ open: false, id: null, reason: '', saving: false, error: null });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [invoicesRes, customersRes, productsRes] = await Promise.all([
        invoiceService.getAll(),
        customerService.getByType('CUSTOMER'),
        productService.getAll({ activeOnly: true }).catch(() => ({ success: false, data: [] }))
      ]);
      if (invoicesRes.success) setInvoices(invoicesRes.data || []);
      if (customersRes.success) setCustomers(customersRes.data || []);
      if (productsRes.success) setProducts(productsRes.data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const formatCurrency = (amount, cur) => {
    if (!amount) return `${cur || 'PKR'} 0`;
    // "PKR 1,615" / "USD 1,615" — Intl's currency style printed "Rs"/"$", unlike the rest of the app
    const c = cur || 'PKR';
    const n = Number(amount);
    const abs = Math.abs(n).toLocaleString('en-US', { maximumFractionDigits: 2 });
    return n < 0 ? `-${c} ${abs}` : `${c} ${abs}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB');
  };

  const getStatusBadge = (status) => ({
    DRAFT: 'badge-draft', SENT: 'badge-sent', PAID: 'badge-paid',
    PARTIAL: 'badge-pending', OVERDUE: 'badge-overdue', CANCELLED: 'badge-draft'
  }[status] || 'badge-draft');

  const showConfirm = (title, message, onConfirm, danger = false) => {
    setConfirmDialog({ open: true, title, message, onConfirm, danger });
  };

  const closeConfirm = () => setConfirmDialog(prev => ({ ...prev, open: false }));

  const handleSend = (id) => {
    showConfirm(
      'Send Invoice',
      'This will mark the invoice as SENT and notify the customer. Continue?',
      async () => {
        closeConfirm();
        try { await invoiceService.send(id); fetchData(); }
        catch (e) { setError(e.message); }
      }
    );
  };

  const handleCancel = (id) => {
    setCancelDialog({ open: true, id, reason: '', saving: false, error: null });
  };

  const submitCancel = async () => {
    setCancelDialog(prev => ({ ...prev, saving: true, error: null }));
    try {
      await invoiceService.cancel(cancelDialog.id, cancelDialog.reason);
      setCancelDialog({ open: false, id: null, reason: '', saving: false, error: null });
      fetchData();
    } catch (e) {
      setCancelDialog(prev => ({ ...prev, saving: false, error: e.message }));
    }
  };

  const handleReopen = (id) => {
    showConfirm(
      'Reopen Invoice',
      'This will move the invoice back to DRAFT so it can be edited. Continue?',
      async () => {
        closeConfirm();
        try { await invoiceService.reopen(id); fetchData(); }
        catch (e) { setError(e.message); }
      }
    );
  };

  // Fetches the invoice PDF blob — used by <PdfActions> for both Preview (inline iframe) and
  // Download (save to disk), added 2026-09-14 so a PDF can be checked before committing to a
  // file, not just downloaded blind.
  const fetchInvoicePdfBlob = async (invoice) => {
    const response = await fetch(`${API_BASE}/invoices/${invoice.id}/pdf`, {
      method: 'GET', credentials: 'include', headers: { Accept: 'application/pdf' }
    });
    if (!response.ok) throw new Error('Failed to load PDF');
    return response.blob();
  };

  const filteredInvoices = invoices.filter(inv => {
    const tabMatch = activeTab === 'all' ? true
      : activeTab === 'overdue' ? (inv.overdue && inv.status !== 'PAID')
      : inv.status === activeTab.toUpperCase();
    if (!tabMatch) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (inv.invoiceNumber || '').toLowerCase().includes(q)
      || (inv.customerName || '').toLowerCase().includes(q)
      || String(inv.totalAmount || '').includes(q);
  });

  if (loading) {
    return (
      <div className="invoice-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <span>Loading invoices...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-management">
      <div className="page-header">
        <h2>Invoice Management</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {(() => { const { headers, matrix } = buildInvoiceRows(filteredInvoices); return (
            <CsvActions filename="invoices" headers={headers} matrix={matrix} />
          ); })()}
          <button onClick={() => { setEditingInvoice(null); setShowForm(true); }} className="btn-primary">
            + New Invoice
          </button>
        </div>
      </div>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Status Tabs */}
      <div className="tab-navigation">
        {[
          { key: 'all', label: 'All', count: invoices.length },
          { key: 'draft', label: 'Draft', count: invoices.filter(i => i.status === 'DRAFT').length },
          { key: 'sent', label: 'Sent', count: invoices.filter(i => i.status === 'SENT').length },
          { key: 'partial', label: 'Partial', count: invoices.filter(i => i.status === 'PARTIAL').length },
          { key: 'paid', label: 'Paid', count: invoices.filter(i => i.status === 'PAID').length },
          { key: 'overdue', label: 'Overdue', count: invoices.filter(i => i.overdue && i.status !== 'PAID').length },
        ].map(t => (
          <button
            key={t.key}
            className={`tab-button ${activeTab === t.key ? 'active' : ''}`}
            onClick={() => setActiveTab(t.key)}
          >
            {t.label} ({t.count})
          </button>
        ))}
      </div>

      {/* Search */}
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          placeholder="Search by invoice #, customer name, or amount..."
          className="form-input"
          style={{ maxWidth: 400, fontSize: '0.9rem' }}
        />
      </div>

      {/* Invoice Table */}
      <div className="data-table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Invoice #</th>
              <th>Customer</th>
              <th>Type</th>
              <th>Date</th>
              <th>Due Date</th>
              <th>Amount</th>
              <th>Balance Due</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.length === 0 ? (
              <tr>
                <td colSpan="9">
                  <div className="empty-state">
                    <div className="empty-state-icon">—</div>
                    <h3>No invoices found</h3>
                    <p>Create your first invoice to get started</p>
                  </div>
                </td>
              </tr>
            ) : (
              filteredInvoices.map(invoice => (
                <tr key={invoice.id} className={invoice.overdue && invoice.status !== 'PAID' ? 'row-overdue' : undefined}>
                  <td><strong>{invoice.invoiceNumber}</strong></td>
                  <td>
                    <div>{invoice.customerName}</div>
                    {invoice.customerLimitExceeded && (
                      <span style={{ fontSize: '0.7rem', color: 'var(--color-error)' }}>Over credit limit</span>
                    )}
                  </td>
                  <td>
                    <span style={{ 
                      fontSize: '0.7rem', 
                      padding: '2px 6px', 
                      borderRadius: '4px',
                      background: invoice.salesType === 'EXPORT' ? 'var(--color-info-light)' : 'var(--color-bg-tertiary)',
                      color: invoice.salesType === 'EXPORT' ? 'var(--color-primary)' : 'var(--color-neutral-700)'
                    }}>
                      {invoice.salesType || 'LOCAL'}
                    </span>
                    {invoice.priceType === 'FIXED_CONTRACT' && (
                      <div style={{ fontSize: '0.65rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>Fixed</div>
                    )}
                  </td>
                  <td>{formatDate(invoice.invoiceDate)}</td>
                  <td>
                    {formatDate(invoice.dueDate)}
                    {invoice.overdue && invoice.status !== 'PAID' && (
                      <div style={{ fontSize: '0.7rem', color: 'var(--color-error)' }}>{invoice.daysOverdue}d overdue</div>
                    )}
                  </td>
                  <td className="nowrap">{formatCurrency(invoice.totalAmount, invoice.currency)}</td>
                  <td className="nowrap" style={{ fontWeight: invoice.balanceDue > 0 ? 700 : 400, color: invoice.balanceDue > 0 ? 'var(--color-error)' : 'var(--color-success)' }}>
                    {formatCurrency(invoice.balanceDue, invoice.currency)}
                  </td>
                  <td>
                    <span className={getStatusBadge(invoice.status)}>
                      {invoice.status}
                    </span>
                  </td>
                  <td>
                    <div className="action-buttons">
                      {invoice.status === 'DRAFT' && (
                        <>
                          <button onClick={() => { setEditingInvoice(invoice); setShowForm(true); }} className="action-btn btn-secondary">
                            Edit
                          </button>
                          <button onClick={() => handleSend(invoice.id)} className="action-btn btn-success">
                            Send
                          </button>
                        </>
                      )}
                      {['SENT', 'PARTIAL'].includes(invoice.status) && (
                        <button onClick={() => setPaymentDialog({ open: true, invoice })} className="action-btn btn-success">
                          Record Payment
                        </button>
                      )}
                      {!['PAID', 'CANCELLED'].includes(invoice.status) && (
                        <button onClick={() => handleCancel(invoice.id)} className="action-btn btn-danger">
                          Cancel
                        </button>
                      )}
                      {isAdmin && ['SENT', 'CANCELLED'].includes(invoice.status) && (
                        <button onClick={() => handleReopen(invoice.id)} className="action-btn btn-secondary" style={{ fontSize: '11px' }}>
                          Reopen
                        </button>
                      )}
                      <PdfActions
                        fetchBlob={() => fetchInvoicePdfBlob(invoice)}
                        filename={`${invoice.invoiceNumber}.pdf`}
                        label="PDF"
                        className="action-btn btn-primary"
                      />
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Invoice Form Modal */}
      {showForm && (
        <InvoiceForm
          invoice={editingInvoice}
          customers={customers}
          products={products}
          onClose={() => { setShowForm(false); setEditingInvoice(null); }}
          onSave={() => { setShowForm(false); setEditingInvoice(null); fetchData(); }}
        />
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={confirmDialog.open}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={closeConfirm}
        danger={confirmDialog.danger}
      />

      {/* Payment Dialog */}
      <PaymentDialog
        open={paymentDialog.open}
        invoice={paymentDialog.invoice}
        onClose={() => setPaymentDialog({ open: false, invoice: null })}
        onSave={() => { setPaymentDialog({ open: false, invoice: null }); fetchData(); }}
      />

      {/* Cancel Invoice — reason required */}
      <Dialog open={cancelDialog.open} onClose={() => !cancelDialog.saving && setCancelDialog(prev => ({ ...prev, open: false }))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>Cancel Invoice</DialogTitle>
        <DialogContent>
          {cancelDialog.error && <Alert severity="error" sx={{ mb: 2 }}>{cancelDialog.error}</Alert>}
          <p style={{ margin: '0 0 12px', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
            This action cannot be undone. Please provide a reason for cancellation.
          </p>
          <TextField
            label="Reason for cancellation"
            multiline
            rows={3}
            value={cancelDialog.reason}
            onChange={e => setCancelDialog(prev => ({ ...prev, reason: e.target.value }))}
            fullWidth
            size="small"
            placeholder="e.g. Customer requested cancellation, duplicate invoice…"
            autoFocus
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <button onClick={() => setCancelDialog(prev => ({ ...prev, open: false }))} className="btn-secondary" disabled={cancelDialog.saving}>
            Back
          </button>
          <button
            onClick={submitCancel}
            className="btn-danger"
            disabled={cancelDialog.saving || !cancelDialog.reason.trim()}
          >
            {cancelDialog.saving ? 'Cancelling…' : 'Cancel Invoice'}
          </button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

// ─── Invoice Form ──────────────────────────────────────────────────────────
const InvoiceForm = ({ invoice, customers, products = [], onClose, onSave }) => {
  const attachRef = useRef(null);
  const { isAdmin } = checkAdminStatus(localStorage.getItem('userData'));
  const [creditBlocked, setCreditBlocked] = useState(false);
  const [formData, setFormData] = useState({
    customerId: invoice?.customerId || '',
    invoiceDate: invoice?.invoiceDate || new Date().toISOString().split('T')[0],
    dueDate: invoice?.dueDate || '',
    taxRate: invoice?.taxRate || 0,
    currency: invoice?.currency || 'PKR',
    salesType: invoice?.salesType || 'LOCAL',
    priceType: invoice?.priceType || 'OPEN_MARKET',
    contractReference: invoice?.contractReference || '',
    notes: invoice?.notes || '',
    batchId: invoice?.batchId || '',
    gatePassId: invoice?.gatePassId || '',
    // Stock now always moves via the linked gate pass's dispatch, never a separate deduction —
    // see InvoiceService.createInvoice(). Kept in payload only for backward-compat with old code
    // paths; always false going forward.
    updateStock: false,
    items: invoice?.items?.length
      ? invoice.items.map(i => ({ productId: i.productId || '', itemName: i.itemName || '', quantity: i.quantity || '', unitPrice: i.unitPrice || '', unit: i.unit || '' }))
      : [{ productId: '', itemName: '', quantity: '', unitPrice: '', unit: '' }]
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [batchList, setBatchList] = useState([]);
  const [gatePassOptions, setGatePassOptions] = useState([]);
  const [gatePassLoading, setGatePassLoading] = useState(false);

  // Every invoice (Local or Export) needs an EXIT gate pass that hasn't been used yet — load the
  // full list once; ISSUED means still available (used ones flip to DISPATCHED automatically).
  useEffect(() => {
    if (invoice) return; // editing an existing invoice — its gate pass is already fixed
    gatePassService.getByType('EXIT')
      .then(r => setGatePassOptions((r.data || r || []).filter(gp => gp.status === 'ISSUED')))
      .catch(() => setGatePassOptions([]));
  }, [invoice]);

  const handleGatePassChange = (e) => {
    const id = e.target.value;
    const gp = gatePassOptions.find(g => String(g.id) === String(id));
    // The gate pass documents who the goods physically left the warehouse for — the invoice's
    // Customer must be that same party, not some other independently-picked one (backend now
    // hard-blocks a mismatch too; this just saves the round trip by getting it right up front).
    // GatePass only stores partyName as free text (no Customer FK), so resolve it the same way
    // PurchaseService.createFromGatePass() already does on the ENTRY side: exact name match.
    const matchedCustomer = gp?.partyName
      ? customers.find(c => c.name?.trim().toLowerCase() === gp.partyName.trim().toLowerCase())
      : null;
    // Customer gets set here directly (not through handleChange's own 'customerId' branch), so
    // replicate its side effects — currency default and, critically, the Due Date auto-calc —
    // or Due Date silently stays empty and required, failing invoice creation with no clear
    // reason. Real bug found 2026-09-14 while live-testing the gate-pass-locked-customer flow
    // (item 22): selecting a Gate Pass before ever touching the Customer field left Due Date
    // blank because only the manual Customer dropdown's onChange used to compute it.
    setFormData(prev => {
      if (!matchedCustomer) {
        return { ...prev, gatePassId: id, batchId: gp?.batchId || '' };
      }
      const country = (matchedCustomer.country || '').toLowerCase();
      const isPakistani = !country || country === 'pakistan' || country === 'pk';
      const dueDate = computeDueDate(prev.invoiceDate, matchedCustomer.paymentTerms);
      return {
        ...prev,
        gatePassId: id,
        batchId: gp?.batchId || '',
        customerId: matchedCustomer.id,
        currency: isPakistani ? 'PKR' : 'USD',
        dueDate,
      };
    });
    if (!gp) return;
    setGatePassLoading(true);
    gatePassService.getById(id)
      .then(r => {
        const full = r.data || r;
        const items = (full.items || []).filter(i => i.productId);
        if (items.length > 0) {
          setFormData(prev => ({
            ...prev,
            items: items.map(i => {
              const product = products.find(p => String(p.id) === String(i.productId));
              const unit = (i.unit || product?.unit || 'KG').toUpperCase();
              // Must match GatePassService.updateProductStock() exactly: weight-based products
              // (KG/TON/G) deduct stock by weight, not the bag/unit count — bill the same number
              // that actually leaves stock, or the invoice quietly under/over-bills the dispatch.
              const isWeightBased = unit === 'KG' || unit === 'TON' || unit === 'G';
              const billQty = (isWeightBased && i.weight && Number(i.weight) > 0) ? i.weight : i.quantity;
              return {
                productId: i.productId,
                itemName: i.itemName || product?.name || '',
                unit: i.unit || product?.unit || 'KG',
                quantity: billQty || '',
                unitPrice: product?.defaultSalePrice || '',
              };
            })
          }));
        }
      })
      .catch(() => {})
      .finally(() => setGatePassLoading(false));
  };

  // Credit status for selected customer
  const [creditStatus, setCreditStatus] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);

  // Latest exchange rate for USD→PKR conversion
  const [latestRate, setLatestRate] = useState(null);

  // Fetch credit status when customer changes
  useEffect(() => {
    if (!formData.customerId) { setCreditStatus(null); return; }
    setCreditLoading(true);
    customerService.getCreditStatus(formData.customerId)
      .then(res => setCreditStatus(res.data || null))
      .catch(() => setCreditStatus(null))
      .finally(() => setCreditLoading(false));
  }, [formData.customerId]);

  // Fetch latest exchange rate when currency is USD
  useEffect(() => {
    if (formData.currency === 'USD') {
      exchangeRateService.getLatest()
        .then(res => setLatestRate(res.data?.netRateAfterFee || res.data?.usdToPkr || null))
        .catch(() => setLatestRate(null));
    } else {
      setLatestRate(null);
    }
  }, [formData.currency]);

  // Due date is always auto-calculated (invoice date + customer payment terms) to avoid manual entry
  // errors — falls back to a 30-day term when the customer has no payment terms configured.
  const DEFAULT_PAYMENT_TERM_DAYS = 30;
  const computeDueDate = (invoiceDateStr, paymentTermsRaw) => {
    const base = invoiceDateStr ? new Date(invoiceDateStr) : new Date();
    const terms = parseInt(paymentTermsRaw, 10);
    const days = Number.isFinite(terms) && terms > 0 ? terms : DEFAULT_PAYMENT_TERM_DAYS;
    base.setDate(base.getDate() + days);
    return base.toISOString().split('T')[0];
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'customerId' && value) {
      const customer = customers.find(c => String(c.id) === String(value));
      if (customer) {
        const country = (customer.country || '').toLowerCase();
        const isPakistani = !country || country === 'pakistan' || country === 'pk';
        const dueDate = computeDueDate(formData.invoiceDate, customer.paymentTerms);
        setFormData(prev => ({ ...prev, [name]: value, currency: isPakistani ? 'PKR' : 'USD', dueDate }));
        return;
      }
    }
    if (name === 'invoiceDate' && value) {
      const customer = customers.find(c => String(c.id) === String(formData.customerId));
      const dueDate = computeDueDate(value, customer?.paymentTerms);
      setFormData(prev => ({ ...prev, [name]: value, dueDate }));
      return;
    }
    // When switching to EXPORT, default stock deduction OFF (gate pass handles it)
    if (name === 'salesType') {
      setFormData(prev => ({ ...prev, [name]: value, updateStock: value !== 'EXPORT' }));
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    if (field === 'productId') {
      const product = products.find(p => String(p.id) === String(value));
      if (product) {
        newItems[index] = {
          ...newItems[index],
          productId: value,
          itemName: product.name,
          unit: product.unit || 'KG',
          unitPrice: product.defaultSalePrice || newItems[index].unitPrice || '',
        };
      } else {
        newItems[index] = { ...newItems[index], productId: '', itemName: '', unit: '' };
      }
    } else {
      newItems[index] = { ...newItems[index], [field]: value };
    }
    setFormData(prev => ({ ...prev, items: newItems }));
  };

  const addItem = () => setFormData(prev => ({
    ...prev, items: [...prev.items, { productId: '', itemName: '', quantity: '', unitPrice: '', unit: '' }]
  }));

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== index) }));
  };

  const subtotal = formData.items.reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unitPrice) || 0), 0);
  const tax = subtotal * (parseFloat(formData.taxRate) || 0) / 100;
  const total = subtotal + tax;

  // Live PKR equivalent for credit limit comparison
  // If USD and no exchange rate available, use 0 — don't compare raw USD as PKR
  const isUsd = formData.currency === 'USD';
  const totalPkr = isUsd ? (latestRate ? Math.round(total * latestRate) : 0) : total;

  const buildPayload = (override = false) => ({
    ...formData,
    customerId: parseInt(formData.customerId),
    taxRate: parseFloat(formData.taxRate) || 0,
    invoiceType: 'INVOICE',
    salesType: formData.salesType,
    priceType: formData.priceType,
    contractReference: formData.priceType === 'FIXED_CONTRACT' ? formData.contractReference : null,
    overrideCreditLimit: override,
    batchId: formData.batchId ? parseInt(formData.batchId) : null,
    gatePassId: formData.gatePassId ? parseInt(formData.gatePassId) : null,
    updateStock: formData.updateStock !== false,
    items: formData.items.map(item => ({
      itemName: item.itemName,
      quantity: parseFloat(item.quantity) || 1,
      unitPrice: parseFloat(item.unitPrice) || 0,
      unit: item.unit || 'pcs',
      productId: item.productId ? parseInt(item.productId) : null
    }))
  });

  const submitInvoice = async (data) => {
    let savedId = invoice?.id;
    if (invoice) {
      await invoiceService.update(invoice.id, data);
    } else {
      const created = await invoiceService.create(data);
      savedId = created?.id || created?.data?.id;
    }
    if (savedId && attachRef.current?.hasPending()) {
      await attachRef.current.uploadPending('INVOICE', savedId);
    }
    onSave();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const badItems = formData.items.filter(i => !i.productId);
    if (badItems.length > 0) { setError('All items must have a product selected from the catalog — free text is not allowed.'); return; }
    const badQty = formData.items.filter(i => !i.quantity || parseFloat(i.quantity) <= 0);
    if (badQty.length > 0) { setError('All items must have a valid quantity.'); return; }
    if (!invoice && !formData.gatePassId) { setError('A Gate Pass (EXIT) is required — select one above before creating the invoice.'); return; }

    setError(null);
    setCreditBlocked(false);
    setSaving(true);
    try {
      await submitInvoice(buildPayload(false));
    } catch (err) {
      const msg = err.message || '';
      if (msg.toLowerCase().includes('credit limit exceeded')) {
        setCreditBlocked(true);
        setError(msg);
      } else {
        setError(msg);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleOverride = async () => {
    setError(null);
    setCreditBlocked(false);
    setSaving(true);
    try {
      await submitInvoice(buildPayload(true));
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '860px', maxHeight: '90vh', overflowY: 'auto' }}>
        <div className="modal-header" style={{ position: 'sticky', top: 0, background: 'white', zIndex: 10, borderBottom: '1px solid var(--color-border-medium)', paddingBottom: 12 }}>
          <h3 style={{ margin: 0 }}>{invoice ? 'Edit Invoice' : 'New Invoice'}</h3>
          <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </div>

        <div style={{ padding: '20px' }}>
          <form onSubmit={handleSubmit}>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
                {creditBlocked && isAdmin && (
                  <button type="button" onClick={handleOverride} disabled={saving}
                    style={{ marginLeft: 12, padding: '4px 12px', background: 'var(--color-error)', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600, fontSize: 12 }}>
                    {saving ? 'Saving...' : 'Admin Override — Create Anyway'}
                  </button>
                )}
              </Alert>
            )}

            {/* Section: Customer & Dates */}
            <div style={{ marginBottom: 20 }}>
              <p style={{ margin: '0 0 12px', fontWeight: 600, color: 'var(--color-neutral-700)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                Customer & Invoice Details
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
                <div className="form-group" style={{ margin: 0 }}>
                  <label>Customer *</label>
                  {(() => {
                    const selectedGatePass = gatePassOptions.find(g => String(g.id) === String(formData.gatePassId));
                    const lockedCustomer = selectedGatePass?.partyName
                      ? customers.find(c => c.name?.trim().toLowerCase() === selectedGatePass.partyName.trim().toLowerCase())
                      : null;
                    if (lockedCustomer) {
                      return (
                        <>
                          <div className="form-input" style={{ background: 'var(--color-bg-tertiary)', color: 'var(--color-text-secondary)', cursor: 'not-allowed' }}>
                            {lockedCustomer.name}{lockedCustomer.customerCode ? ` (${lockedCustomer.customerCode})` : ''}
                          </div>
                          <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)', display: 'block', marginTop: 2 }}>
                            Locked from Gate Pass — it was issued to this customer. Change the Gate Pass above to invoice someone else.
                          </span>
                        </>
                      );
                    }
                    return (
                      <SearchableSelect
                        required
                        placeholder="Select Customer..."
                        value={formData.customerId}
                        options={customers.map(c => ({ value: c.id, label: `${c.name}${c.customerCode ? ` (${c.customerCode})` : ''}` }))}
                        onChange={(value) => handleChange({ target: { name: 'customerId', value } })}
                      />
                    );
                  })()}
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label>Invoice Date *</label>
                  <input type="date" name="invoiceDate" value={formData.invoiceDate} onChange={handleChange} className="form-input" required />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label>Due Date * <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400, fontSize: 11 }}>(auto)</span></label>
                  <input
                    type="date"
                    name="dueDate"
                    value={formData.dueDate}
                    className="form-input"
                    readOnly
                    required
                    title="Auto-calculated from invoice date + customer payment terms — select a customer to set it"
                    style={{ background: 'var(--color-bg-tertiary)', cursor: 'not-allowed' }}
                  />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label>Currency</label>
                  <select name="currency" value={formData.currency} onChange={handleChange} className="form-select">
                    <option value="PKR">PKR</option>
                    <option value="USD">USD</option>
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label>Tax / GST Rate (%)</label>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                    {[0, 5, 8, 10, 17].map(rate => (
                      <button
                        key={rate}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, taxRate: rate }))}
                        style={{
                          padding: '3px 10px', fontSize: 12, borderRadius: 4, border: '1px solid var(--color-border-strong)', cursor: 'pointer',
                          background: Number(formData.taxRate) === rate ? 'var(--color-primary)' : 'var(--color-neutral-50)',
                          color: Number(formData.taxRate) === rate ? '#fff' : 'var(--color-neutral-700)',
                          fontWeight: Number(formData.taxRate) === rate ? 700 : 400,
                        }}
                      >
                        {rate}%
                      </button>
                    ))}
                  </div>
                  <input type="number" name="taxRate" value={formData.taxRate} onChange={handleChange} className="form-input" min="0" max="100" step="0.01" placeholder="or type custom %" style={{ width: '100%' }} />
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label>Sales Type *</label>
                  <select name="salesType" value={formData.salesType} onChange={handleChange} className="form-select">
                    <option value="LOCAL">Local (Domestic)</option>
                    <option value="EXPORT">Export (International)</option>
                  </select>
                </div>

                <div className="form-group" style={{ margin: 0 }}>
                  <label>Price Type *</label>
                  <select name="priceType" value={formData.priceType} onChange={handleChange} className="form-select">
                    <option value="OPEN_MARKET">Open Market</option>
                    <option value="FIXED_CONTRACT">Fixed Contract</option>
                  </select>
                </div>

                {!invoice && (
                  <div className="form-group" style={{ margin: 0, gridColumn: gatePassOptions.length === 0 ? 'span 2' : undefined }}>
                    <label style={{ color: formData.gatePassId ? 'var(--color-success)' : 'var(--color-error)' }}>
                      Gate Pass (EXIT) * <span style={{ color: 'var(--color-text-secondary)', fontWeight: 400 }}>(required — proves what physically left the warehouse)</span>
                    </label>
                    {gatePassOptions.length > 0 ? (
                      <select
                        name="gatePassId"
                        value={formData.gatePassId}
                        onChange={handleGatePassChange}
                        className="form-select"
                        style={{ borderColor: formData.gatePassId ? 'var(--color-success)' : 'var(--color-error)' }}
                      >
                        <option value="">— Select Gate Pass —</option>
                        {gatePassOptions.map(gp => (
                          <option key={gp.id} value={gp.id}>
                            {gp.gatePassNumber} – {gp.partyName}{gp.batchCode ? ` · ${gp.batchCode}` : ''}{gp.containerNumber ? ` · ${gp.containerNumber}` : ''}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <div style={{ background: 'var(--color-error-light)', border: '1px solid var(--color-error-border)', borderRadius: 6, padding: '8px 12px', fontSize: 12, color: 'var(--color-error)' }}>
                        No EXIT gate passes available. Go to <strong>Gate Passes → New</strong>, set Type = EXIT, issue it, then come back and link it here.
                      </div>
                    )}
                    {gatePassLoading && <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>Loading gate pass items…</span>}
                    {formData.batchId && (
                      <span style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>Batch auto-linked from this gate pass.</span>
                    )}
                  </div>
                )}

                {formData.priceType === 'FIXED_CONTRACT' && (
                  <div className="form-group" style={{ margin: 0 }}>
                    <label>Contract Reference</label>
                    <input type="text" name="contractReference" value={formData.contractReference} onChange={handleChange} className="form-input" placeholder="e.g. CONTRACT-2026-001" />
                  </div>
                )}
              </div>
            </div>

            {/* Credit Status Banner — shown live when customer is selected */}
            <CreditBanner
              status={creditStatus}
              loading={creditLoading}
              thisInvoicePkr={totalPkr}
              thisInvoiceCurrency={formData.currency}
              thisInvoiceTotal={total}
              exchangeRate={latestRate}
            />

            {/* Section: Items */}
            <div style={{ marginBottom: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <div>
                  <p style={{ margin: '0 0 2px', fontWeight: 600, color: 'var(--color-neutral-700)', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    Invoice Items
                  </p>
                  <span style={{ fontSize: 11, color: formData.salesType === 'LOCAL' ? 'var(--color-success)' : 'var(--color-text-secondary)' }}>
                    {formData.salesType === 'LOCAL'
                      ? 'Stock will be deducted when the invoice is saved'
                      : 'EXPORT — stock is controlled by the Gate Pass exit, not the invoice'}
                  </span>
                </div>
                <button type="button" onClick={addItem} className="btn-secondary" style={{ fontSize: '0.85rem', padding: '6px 14px' }}>
                  + Add Item
                </button>
              </div>

              {/* Mobile: stacked cards. Desktop: table */}
              <div className="invoice-items-desktop">
                <table className="data-table" style={{ marginBottom: 0 }}>
                  <thead>
                    <tr>
                      <th>Product * <span style={{ fontWeight: 400, fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>(select from catalog)</span></th>
                      <th style={{ width: 120 }}>Qty *</th>
                      <th style={{ width: 140 }}>Unit Price *</th>
                      <th style={{ width: 130, textAlign: 'right' }}>Amount</th>
                      <th style={{ width: 40 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {formData.items.map((item, index) => (
                      <tr key={index}>
                        <td>
                          <SearchableSelect
                            required
                            placeholder={products.length ? '— Select Product —' : '— No products in catalog —'}
                            value={item.productId || ''}
                            options={products.map(p => ({
                              value: p.id,
                              label: `${p.name}${p.productCode ? ` [${p.productCode}]` : ''}${p.unit ? ` · ${p.unit}` : ''}${p.currentStock != null ? ` · Stock: ${p.currentStock}` : ''}`,
                            }))}
                            onChange={(value) => handleItemChange(index, 'productId', value)}
                            style={{ fontSize: '0.8rem', marginBottom: 4 }}
                          />
                          {item.itemName && (
                            <div style={{ fontSize: '0.75rem', color: 'var(--color-text-secondary)', fontStyle: 'italic' }}>
                              {item.itemName}{item.unit ? ` · ${item.unit}` : ''}
                            </div>
                          )}
                        </td>
                        <td>
                          <input type="number" value={item.quantity} onChange={e => handleItemChange(index, 'quantity', e.target.value)} className="form-input" min="0.01" step="0.01" required placeholder="0" style={{ minWidth: 90 }} />
                        </td>
                        <td>
                          <input type="number" value={item.unitPrice} onChange={e => handleItemChange(index, 'unitPrice', e.target.value)} className="form-input" min="0" step="0.01" required placeholder="0.00" style={{ minWidth: 100 }} />
                          {item.unitPrice && item.unit && <div style={{ fontSize: '0.7rem', color: 'var(--color-text-secondary)' }}>per {item.unit}</div>}
                        </td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>
                          {formData.currency} {((parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)).toLocaleString()}
                        </td>
                        <td>
                          <button type="button" onClick={() => removeItem(index)} className="btn-danger" style={{ padding: '4px 8px', fontSize: '1rem' }} disabled={formData.items.length === 1}>
                            ×
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile stacked items */}
              <div className="invoice-items-mobile">
                {formData.items.map((item, index) => (
                  <div key={index} style={{ border: '1px solid var(--color-border-medium)', borderRadius: 8, padding: 12, marginBottom: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <span style={{ fontWeight: 600, color: 'var(--color-neutral-700)', fontSize: '0.85rem' }}>Item {index + 1}</span>
                      <button type="button" onClick={() => removeItem(index)} className="btn-danger" style={{ padding: '2px 8px' }} disabled={formData.items.length === 1}>×</button>
                    </div>
                    <div style={{ display: 'grid', gap: 8 }}>
                      <div className="form-group" style={{ margin: 0 }}>
                        <label style={{ fontSize: '0.8rem', color: !item.productId ? 'var(--color-error)' : 'var(--color-neutral-700)' }}>Product *</label>
                        <SearchableSelect
                          required
                          placeholder={products.length ? '— Select Product —' : '— No products —'}
                          value={item.productId || ''}
                          options={products.map(p => ({
                            value: p.id,
                            label: `${p.name}${p.productCode ? ` [${p.productCode}]` : ''}${p.currentStock != null ? ` · ${p.currentStock} ${p.unit || ''}` : ''}`,
                          }))}
                          onChange={(value) => handleItemChange(index, 'productId', value)}
                        />
                        {item.itemName && <div style={{ fontSize: '0.72rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>{item.itemName}{item.unit ? ` · ${item.unit}` : ''}</div>}
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label style={{ fontSize: '0.8rem' }}>Qty *</label>
                          <input type="number" value={item.quantity} onChange={e => handleItemChange(index, 'quantity', e.target.value)} className="form-input" min="0.01" step="0.01" required placeholder="0" style={{ width: '100%' }} />
                        </div>
                        <div className="form-group" style={{ margin: 0 }}>
                          <label style={{ fontSize: '0.8rem' }}>Unit Price ({formData.currency}) *</label>
                          <input type="number" value={item.unitPrice} onChange={e => handleItemChange(index, 'unitPrice', e.target.value)} className="form-input" min="0" step="0.01" required placeholder="0.00" style={{ width: '100%' }} />
                        </div>
                      </div>
                      <div style={{ textAlign: 'right', fontWeight: 700, color: 'var(--color-neutral-700)' }}>
                        Total: {formData.currency} {((parseFloat(item.quantity) || 0) * (parseFloat(item.unitPrice) || 0)).toLocaleString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div style={{ background: 'var(--color-neutral-50)', borderRadius: 8, padding: '12px 16px', marginTop: 12, textAlign: 'right' }}>
                <div style={{ color: 'var(--color-text-secondary)', marginBottom: 4 }}>Subtotal: <strong>{formData.currency} {subtotal.toLocaleString()}</strong></div>
                {parseFloat(formData.taxRate) > 0 && (
                  <div style={{ color: 'var(--color-text-secondary)', marginBottom: 4 }}>Tax ({formData.taxRate}%): <strong>{formData.currency} {tax.toLocaleString()}</strong></div>
                )}
                <div style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--color-text-primary)' }}>
                  Total: {formData.currency} {total.toLocaleString()}
                </div>
                {isUsd && latestRate && total > 0 && (
                  <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem', marginTop: 4 }}>
                    = PKR {totalPkr.toLocaleString()} <span style={{ opacity: 0.7 }}>@ {latestRate.toFixed(2)}/USD</span>
                  </div>
                )}
                {isUsd && !latestRate && (
                  <div style={{ color: 'var(--color-error)', fontSize: '0.82rem', marginTop: 4 }}>
                    No exchange rate found — add a rate in Exchange Rates to see PKR equivalent
                  </div>
                )}
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: 16 }}>
              <label>Notes</label>
              <textarea name="notes" value={formData.notes} onChange={handleChange} className="form-textarea" rows="3" placeholder="Optional notes or payment terms..." />
            </div>

            <AttachmentPanel ref={attachRef} entityType="INVOICE" entityId={invoice?.id} />

            {/* Stock now always moves via the linked gate pass's dispatch — one trigger, no
                separate toggle to remember (that's what used to cause double-deduction). */}
            {!invoice && (
              <div style={{ background: 'var(--color-info-light)', border: '1px solid var(--color-info-border)', borderRadius: '8px', padding: '12px', marginBottom: '16px' }}>
                <span style={{ fontSize: '12px', color: 'var(--color-info-strong)' }}>
                  <strong>Stock will be deducted automatically</strong> when this invoice is created, via the linked Gate Pass's dispatch — no separate step needed.
                </span>
              </div>
            )}

            {/* Footer */}
            <div className="modal-footer" style={{ borderTop: '1px solid var(--color-border-medium)', paddingTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button type="button" onClick={onClose} className="btn-secondary" disabled={saving}>Cancel</button>
              <button type="submit" className="btn-primary" disabled={saving || !formData.customerId}>
                {saving ? 'Saving...' : (invoice ? 'Update Invoice' : 'Create Invoice')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InvoiceManagement;
